// ProtectedRoute.tsx
import React, { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { fetchUserInfo } from 'redux/slices/user/userSlice';
import client from 'api/apiClient';
import { fetchMoneyContainer } from 'redux/slices/wallet/moneyContainerSlice';
import { fetchMerchantBalance } from 'redux/slices/wallet/merchantBalanceSlice';

interface ProtectedRouteProps {
    children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { auth, user, isMerchant } = useSelector((state: RootState) => ({
        auth: state.auth?.data,
        user: state.user,
        isMerchant: state.auth.isMerchant,
    }));
    const { access_token: accessToken, refresh_token: refreshToken, need_change_password } = auth || {};
    const id = user?.userInfo?.id;
    const companyId = user?.defaultCompanyId;

    useEffect(() => {
        // need_change_password is checked for false because when it's true we need to navigate to change password and if userInfo response
        // is invalid the userInfo action logout the user due to this. So we will not be able to show the change password screen without this.
        if (!need_change_password && accessToken) {
            dispatch(fetchUserInfo());
        }
    }, [dispatch, accessToken]);

    useEffect(() => {
        if (accessToken) {
            client.setToken(accessToken, refreshToken);
        }

        if (accessToken && id) {
            dispatch(fetchMoneyContainer());
            isMerchant && dispatch(fetchMerchantBalance());
        }
    }, [accessToken, dispatch, id, refreshToken, companyId, isMerchant]);

    if (!accessToken) {
        // Redirect to the login page if user information is not available
        return <Navigate to="/login" />;
    }

    // Render the protected route if user information is available
    return <Outlet />;
};

export default ProtectedRoute;
